import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDefaultContacts } from "../../../redux/actions/careplanActions";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import Spacer from "../../common/Spacer";
import { EditIcon, QRIcon, LogIcon } from "../../common/Icons";
import FloatingMenu from "../../common/FloatingMenu";

const ViewSection = ({
  section,
  onClose,
  onEditSection,
  onShowUpdateQR,
  onShowAuditLogs,
}) => {
  const dispatch = useDispatch();
  const { defaultContactsLoaded, carePlan } = useSelector((s) => s.careplan);
  const defaultContacts = carePlan.contacts;

  useEffect(() => {
    if (section.id === 6 && !defaultContactsLoaded) {
      dispatch(fetchDefaultContacts());
    }
  }, [section.id, defaultContactsLoaded, dispatch]);

  const menuItems = () => {
    const all = [
      {
        label: "Edit",
        icon: <EditIcon color="#004F96" />,
        action: () => onEditSection(section),
      },
      {
        label: "Update QR code",
        icon: <QRIcon color="#004F96" />,
        action: () => onShowUpdateQR(section),
      },
      {
        label: "Audit log",
        icon: <LogIcon color="#004F96" />,
        action: () => onShowAuditLogs(section),
      },
    ];

    if (section.id === 6) {
      return all.filter(({ label }) => label !== "Update QR code");
    }

    return all;
  };

  if (!section) {
    return null; // or a loading spinner
  }

  return (
    <FullScreenOverlay
      title="View Section"
      iconClose={true}
      onClose={onClose}
      width="100%"
      height="100%"
    >
      <FormSection>
        <p className="body-text-large medium neutral-600">Section title</p>
        <Spacer height="8px" />
        <FormTitle className="semi-bold">{section.title}</FormTitle>
        <Spacer height="32px" />
        {section.isServiceUserSpecific && <p>Service user specific section</p>}
      </FormSection>

      {section.id === 6 ? (
        <FormSection>
          <h6 className="semi-bold">Default Contacts</h6>
          <table className="care-plan-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>
              {defaultContacts.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </FormSection>
      ) : (
        <FormSection>
          {section.items.map((item) => (
            <SectionCard key={item.id}>
              <ItemTitle className="semi-bold">{item.title}</ItemTitle>
              <ItemCard>
                <ItemThumbnail>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.title} />
                  ) : (
                    <div></div>
                  )}
                </ItemThumbnail>
                <ItemContent>
                  <p className="body-text-regular regular neutral-700">
                    Item Description
                  </p>
                  <ItemDescription className="body-text-regular regular neutral-600">
                    {item.description}
                  </ItemDescription>
                </ItemContent>
              </ItemCard>
            </SectionCard>
          ))}
        </FormSection>
      )}

      <FloatingMenu items={menuItems()} position="bottom-center" offset={24} />
    </FullScreenOverlay>
  );
};

export default ViewSection;

const FormSection = styled.div`
  width: 60%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const FormTitle = styled.h5``;

const SectionCard = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: ${(props) => (props.mode === "edit" ? "0px" : "24px")};
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-200, #f8f9fa);
  pointer-events: auto;

  &: active {
    opacity: 0.5;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2); // Clear visual cue that the item is being dragged
    // cursor: grabbing; // Change cursor to grabbing when the item is being dragged
  }
`;

const ItemTitle = styled.h6``;

const ItemCard = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;
`;

const ItemThumbnail = styled.div`
  width: 150px;
  height: 150px;
  background-color: #d9d9d9;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #d9d9d9);
  overflow: hidden; /* Ensure that the image does not overflow the container */

  img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Ensures the image covers the container without distortion */
    border-radius: 12px; /* Make sure the image has rounded corners like the container */
  }
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const ItemDescription = styled.div`
  display: flex;
  flex: 1 0 0;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #ced4da);
  min-height: 96px;
  background-color: ${(props) =>
    props.mode === "edit" ? "#fff" : "transparent"};

  /* Dynamic height handling  *******NOT GROWING TO 150 NEED TO CHECK******* */
  max-height: 150px; /* Set the maximum height for the description */
  overflow-y: auto; /* Add a scrollbar if the content exceeds the height */
  word-wrap: break-word; /* Ensure long words are wrapped */
  line-height: 1.5; /* Adjust line height to provide better readability */

  /* Allow the element to grow up to the max-height */
  height: auto; /* Let the height grow based on content */
  min-height: 96px; /* Set an initial minimum height */

  /* Optional: Scrollbar styling for better visibility */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #ced4da transparent; /* Firefox */

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ced4da; /* Scrollbar color */
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent; /* Scrollbar track color */
  }
`;
